import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Plus, Trash, Broadcast, Shield, Circle } from "@phosphor-icons/react";

const statusBadge = (status) => {
  if (status === "primary") return "border-teal/40 bg-teal/15 text-teal";
  if (status === "backup") return "border-gold/40 bg-gold/15 text-gold";
  return "border-navy/20 bg-navy/5 text-navy/60";
};

export default function OperatorsPanel({ island, onChange }) {
  const [ops, setOps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openAdd, setOpenAdd] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/admin/operators", { params: { island_slug: island } })
      .then((r) => setOps(r.data))
      .catch(() => setOps([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [island]);

  const activate = async (op, role) => {
    if (!op.informed_consent) {
      toast.error("Cannot activate — informed consent is required first.");
      return;
    }
    try {
      await api.post(`/admin/operators/${op.id}/activate`, null, { params: { role } });
      toast.success(`${op.company_name} is now the ${role.toUpperCase()} operator.`);
      load(); onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to activate.");
    }
  };

  const deactivate = async (op) => {
    try {
      await api.post(`/admin/operators/${op.id}/deactivate`);
      toast.success(`${op.company_name} deactivated.`);
      load(); onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to deactivate.");
    }
  };

  const toggleConsent = async (op) => {
    try {
      await api.patch(`/admin/operators/${op.id}`, { informed_consent: !op.informed_consent });
      toast.success("Consent updated.");
      load(); onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update.");
    }
  };

  const remove = async (op) => {
    if (!window.confirm(`Delete operator ${op.company_name}?`)) return;
    try {
      await api.delete(`/admin/operators/${op.id}`);
      toast.success("Deleted.");
      load(); onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete.");
    }
  };

  const primary = ops.find((o) => o.status === "primary");
  const backup = ops.find((o) => o.status === "backup");

  return (
    <div data-testid="operators-panel">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
        <div>
          <h2 className="font-display text-2xl text-navy">Operators</h2>
          <p className="text-navy/60 text-sm">
            One PRIMARY + one optional BACKUP per island. Backup rings if primary doesn't answer. Consent required for both.
          </p>
        </div>
        <Dialog open={openAdd} onOpenChange={setOpenAdd}>
          <DialogTrigger asChild>
            <button
              data-testid="add-operator-button"
              className="inline-flex items-center gap-2 bg-navy text-cream px-4 py-2 text-[11px] uppercase tracking-[0.24em] hover:bg-gold hover:text-navy transition-colors"
            >
              <Plus size={16} weight="thin" /> Add operator
            </button>
          </DialogTrigger>
          <AddOperatorDialog island={island} onDone={() => { setOpenAdd(false); load(); onChange?.(); }} />
        </Dialog>
      </div>

      <div className="grid sm:grid-cols-2 gap-4 mb-6" data-testid="routing-summary">
        <div className="border border-teal/40 bg-teal/5 p-5">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.24em] text-teal mb-2">
            <Broadcast size={14} weight="thin" /> Primary (live routing)
          </div>
          <div className="font-display text-2xl text-navy" data-testid="routing-primary">
            {primary ? primary.company_name : <span className="text-navy/40">— none —</span>}
          </div>
        </div>
        <div className="border border-gold/40 bg-gold/5 p-5">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.24em] text-gold mb-2">
            <Shield size={14} weight="thin" /> Backup (fallback)
          </div>
          <div className="font-display text-2xl text-navy" data-testid="routing-backup">
            {backup ? backup.company_name : <span className="text-navy/40">— none —</span>}
          </div>
        </div>
      </div>

      <div className="border border-cream2 bg-cream/60">
        <Table>
          <TableHeader>
            <TableRow className="border-cream2 hover:bg-transparent">
              <TableHead className="text-[10px] uppercase tracking-[0.22em] text-navy/50">Company</TableHead>
              <TableHead className="text-[10px] uppercase tracking-[0.22em] text-navy/50">Phone</TableHead>
              <TableHead className="text-[10px] uppercase tracking-[0.22em] text-navy/50">Consent</TableHead>
              <TableHead className="text-[10px] uppercase tracking-[0.22em] text-navy/50">Status</TableHead>
              <TableHead className="text-[10px] uppercase tracking-[0.22em] text-navy/50 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={5} className="text-center text-navy/50 py-10">Loading…</TableCell></TableRow>
            ) : ops.length === 0 ? (
              <TableRow><TableCell colSpan={5} className="text-center text-navy/50 py-16 font-display text-xl">No operators yet — add one to begin routing.</TableCell></TableRow>
            ) : ops.map((op) => (
              <TableRow key={op.id} className="border-cream2" data-testid={`operator-row-${op.id}`}>
                <TableCell className="text-navy font-medium">{op.company_name}</TableCell>
                <TableCell className="text-navy/80 font-mono text-sm">{op.contact_phone}</TableCell>
                <TableCell>
                  <label className="inline-flex items-center gap-2 cursor-pointer">
                    <Checkbox checked={op.informed_consent} onCheckedChange={() => toggleConsent(op)} data-testid={`consent-toggle-${op.id}`} />
                    <span className="text-xs text-navy/70">
                      {op.informed_consent ? `Consented ${op.informed_date ? new Date(op.informed_date).toLocaleDateString() : ""}` : "Not consented"}
                    </span>
                  </label>
                </TableCell>
                <TableCell>
                  <span className={`inline-block text-[10px] uppercase tracking-[0.22em] px-2 py-1 border ${statusBadge(op.status)}`} data-testid={`status-badge-${op.id}`}>
                    {op.status === "primary" ? "Primary · Live" : op.status === "backup" ? "Backup · Fallback" : op.status}
                  </span>
                </TableCell>
                <TableCell className="text-right">
                  <div className="inline-flex flex-wrap justify-end gap-2">
                    <button
                      disabled={!op.informed_consent || op.status === "primary"}
                      onClick={() => activate(op, "primary")}
                      data-testid={`activate-primary-${op.id}`}
                      className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] px-3 py-1.5 border border-teal text-teal hover:bg-teal hover:text-cream transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <Broadcast size={14} weight="thin" /> Primary
                    </button>
                    <button
                      disabled={!op.informed_consent || op.status === "backup"}
                      onClick={() => activate(op, "backup")}
                      data-testid={`activate-backup-${op.id}`}
                      className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] px-3 py-1.5 border border-gold text-gold hover:bg-gold hover:text-navy transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <Shield size={14} weight="thin" /> Backup
                    </button>
                    {(op.status === "primary" || op.status === "backup") && (
                      <button
                        onClick={() => deactivate(op)}
                        data-testid={`deactivate-op-${op.id}`}
                        className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] px-3 py-1.5 border border-navy/40 text-navy/70 hover:bg-navy hover:text-cream transition-colors"
                      >
                        <Circle size={14} weight="thin" /> Off
                      </button>
                    )}
                    <button
                      onClick={() => remove(op)}
                      data-testid={`delete-op-${op.id}`}
                      className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] px-3 py-1.5 border border-destructive/40 text-destructive hover:bg-destructive hover:text-cream transition-colors"
                    >
                      <Trash size={14} weight="thin" />
                    </button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function AddOperatorDialog({ island, onDone }) {
  const [form, setForm] = useState({ company_name: "", contact_phone: "", informed_consent: false, notes: "" });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.company_name.trim() || !form.contact_phone.trim()) {
      toast.error("Company name and phone are required.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/admin/operators", { ...form, island_slug: island });
      toast.success("Operator added.");
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add.");
    } finally { setSaving(false); }
  };

  return (
    <DialogContent className="rounded-none bg-cream border-cream2" data-testid="add-operator-dialog">
      <DialogHeader>
        <DialogTitle className="font-display text-3xl text-navy">New operator</DialogTitle>
      </DialogHeader>
      <form onSubmit={submit} className="space-y-5">
        <div>
          <label className="text-[10px] uppercase tracking-[0.22em] text-navy/60">Company name (internal only)</label>
          <Input
            data-testid="op-form-name"
            className="rounded-none mt-2 border-navy/20"
            value={form.company_name}
            onChange={(e) => setForm({ ...form, company_name: e.target.value })}
          />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.22em] text-navy/60">Contact phone (E.164)</label>
          <Input
            data-testid="op-form-phone"
            className="rounded-none mt-2 border-navy/20 font-mono"
            placeholder="+12975551234"
            value={form.contact_phone}
            onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
          />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.22em] text-navy/60">Notes</label>
          <Textarea
            data-testid="op-form-notes"
            className="rounded-none mt-2 border-navy/20"
            rows={3}
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
          />
        </div>
        <label className="flex items-start gap-3 cursor-pointer" data-testid="op-form-consent-row">
          <Checkbox
            checked={form.informed_consent}
            onCheckedChange={(v) => setForm({ ...form, informed_consent: !!v })}
            data-testid="op-form-consent"
            className="mt-1"
          />
          <span className="text-sm text-navy/80 leading-snug">
            I confirm this operator has been informed that LocalRida will forward inbound leads to them —
            including as a fallback (backup) — and has consented to receive these calls.
          </span>
        </label>
        <div className="flex justify-end gap-3 pt-2">
          <button
            type="submit"
            disabled={saving}
            data-testid="op-form-submit"
            className="bg-navy text-cream px-6 py-3 text-[11px] uppercase tracking-[0.24em] hover:bg-gold hover:text-navy transition-colors disabled:opacity-60"
          >
            {saving ? "Saving…" : "Save operator"}
          </button>
        </div>
      </form>
    </DialogContent>
  );
}
